import { useEffect, useId, useMemo, useRef, useState } from 'react';
import './SuggestInput.css';

/**
 * Typeable input + custom suggestion list (browser datalist is too small to style).
 */
export default function SuggestInput({
  id,
  value = '',
  options = [],
  disabled = false,
  placeholder = '',
  inputMode = 'text',
  spellCheck = false,
  'aria-label': ariaLabel,
  onChange,
  onPick,
  filterOptions,
  maxItems = 40,
}) {
  const autoId = useId();
  const inputId = id || autoId;
  const listId = `${inputId}-suggest`;
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const filtered = useMemo(() => {
    const q = String(value || '').trim().toLowerCase();
    const source = Array.isArray(options) ? options : [];
    const next = filterOptions
      ? filterOptions(source, q)
      : source.filter((opt) => {
          const label = String(opt.label ?? opt.value ?? opt).toLowerCase();
          return !q || label.includes(q);
        });
    return next.slice(0, maxItems);
  }, [options, value, filterOptions, maxItems]);

  useEffect(() => {
    function onDocPointer(e) {
      if (!rootRef.current?.contains(e.target)) {
        setOpen(false);
        setActiveIndex(-1);
      }
    }
    document.addEventListener('pointerdown', onDocPointer);
    return () => document.removeEventListener('pointerdown', onDocPointer);
  }, []);

  useEffect(() => {
    setActiveIndex(-1);
  }, [value]);

  function emitChange(next) {
    onChange?.(next);
  }

  function pick(opt) {
    const label = String(opt.label ?? opt.value ?? opt);
    onPick?.(opt);
    onChange?.(label);
    setOpen(false);
    setActiveIndex(-1);
  }

  function onKeyDown(e) {
    if (disabled) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === 'Enter' && open && activeIndex >= 0 && filtered[activeIndex]) {
      e.preventDefault();
      pick(filtered[activeIndex]);
      return;
    }
    if (e.key === 'Escape') {
      setOpen(false);
      setActiveIndex(-1);
    }
  }

  const showList = open && !disabled && filtered.length > 0;

  return (
    <div className={`suggest-input${showList ? ' is-open' : ''}`} ref={rootRef}>
      <input
        id={inputId}
        role="combobox"
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-label={ariaLabel}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        autoComplete="off"
        spellCheck={spellCheck}
        inputMode={inputMode}
        onChange={(e) => {
          emitChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {showList ? (
        <ul id={listId} className="suggest-input-list" role="listbox">
          {filtered.map((opt, index) => {
            const label = String(opt.label ?? opt.value ?? opt);
            const key = String(opt.value ?? label);
            return (
              <li key={key} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={index === activeIndex}
                  className={`suggest-input-option${index === activeIndex ? ' is-active' : ''}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(opt)}
                >
                  {label}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
